import { useState, useMemo, useRef, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import FilterSidebar from "./components/FilterSidebar";
import ProductCard from "./components/ProductCard";
import { products } from "@/mocks/products";
import { useLocalized } from "@/lib/localize";
import { useBilingual } from "@/lib/bilingual";

const ProductsPage = () => {
  const [searchParams] = useSearchParams();
  const initialCategory = searchParams.get("category") || "all";
  const loc = useLocalized();
  const b = useBilingual();

  const [filters, setFilters] = useState({ category: initialCategory });
  const [search, setSearch] = useState("");
  const [cols, setCols] = useState<2 | 3 | 4>(3);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  const activeFilterCount = filters.category !== "all" ? 1 : 0;

  // Switching to a smaller category shrinks the page below the current scroll
  // position, so the browser strands the viewport at the footer and the
  // filtered products end up off-screen above. Bring the results back into
  // view whenever the category changes (skip the initial mount).
  const resultsTopRef = useRef<HTMLDivElement>(null);
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    resultsTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [filters.category]);

  const filtered = useMemo(() => {
    let list = [...products];
    if (filters.category !== "all") list = list.filter((p) => p.category === filters.category);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((p) => {
        const haystack = [p.id, loc(p.name), p.category, loc(p.material)]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(q);
      });
    }
    return list;
  }, [filters, search, loc]);

  // Thumbnail-size grid. Each size pins a target thumbnail WIDTH via
  // auto-fill minmax, so narrowing the window drops the column count one step
  // at a time while the thumbnail size stays put, instead of the old fixed
  // column counts that made thumbnails suddenly balloon when crossing a
  // breakpoint (David, 2026-07-27). Below md the toggle is hidden, so mobile
  // keeps a stable 2-up grid regardless of the selected size.
  const gridClass =
    cols === 2
      ? "grid-cols-2 md:grid-cols-[repeat(auto-fill,minmax(320px,1fr))]"
      : cols === 4
      ? "grid-cols-2 md:grid-cols-[repeat(auto-fill,minmax(200px,1fr))]"
      : "grid-cols-2 md:grid-cols-[repeat(auto-fill,minmax(250px,1fr))]";

  // Thumbnail-size toggle. The glyph is an n×n grid of cells whose density
  // mirrors the resulting thumbnail size: one big cell = large thumbnails
  // (fewer columns), a dense 3×3 = small thumbnails (more columns). This
  // reads as a size control rather than page numbers (David, 2026-07-27).
  const sizeOptions = [
    { cols: 2 as const, cells: 1, label: b("Large thumbnails", "大圖") },
    { cols: 3 as const, cells: 2, label: b("Medium thumbnails", "中") },
    { cols: 4 as const, cells: 3, label: b("Small thumbnails", "小圖") },
  ];

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />

      {/* Page Header */}
      <div className="bg-stone-900 pt-24 pb-8 md:pt-32 md:pb-14">
        <div className="max-w-screen-xl mx-auto px-6">
          <nav className="flex items-center gap-2 text-xs text-stone-500 mb-4">
            <Link to="/" className="hover:text-amber-400 transition-colors cursor-pointer">
              {b("Home", "首頁")}
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-stone-300">{b("Products", "產品")}</span>
          </nav>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-3">
            {b("Product Catalog", "產品型錄")}
          </h1>
          <p className="text-stone-400 text-sm md:text-base max-w-xl">
            {b(
              "Forwaro's export furniture catalogue. OEM / ODM available. Contact us for full specifications and a quotation.",
              "Forwaro 外銷家具型錄。支援 OEM／ODM。完整規格與報價請與我們聯繫。",
            )}
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div ref={resultsTopRef} className="max-w-screen-xl mx-auto px-6 py-12 scroll-mt-24">
        {/* Search Bar */}
        <div className="relative mb-8 max-w-lg">
          <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-stone-400 text-sm" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={b(
              "Search products, categories, materials...",
              "搜尋產品、分類、材質…",
            )}
            className="w-full bg-white border border-stone-200 rounded-full pl-10 pr-5 py-3 text-sm text-stone-700 placeholder-stone-400 focus:outline-none focus:border-amber-500 transition-colors"
          />
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          <FilterSidebar
            filters={filters}
            onChange={setFilters}
            mobileOpen={filterDrawerOpen}
            onMobileClose={() => setFilterDrawerOpen(false)}
          />

          <div className="flex-1 min-w-0">
            <div className="flex flex-row items-center justify-between gap-3 mb-6 flex-wrap">
              <button
                onClick={() => setFilterDrawerOpen(true)}
                className="lg:hidden flex items-center gap-2 bg-white border border-stone-200 rounded-full px-4 py-2 text-sm text-stone-700 font-semibold cursor-pointer"
              >
                <i className="ri-filter-3-line" />
                {b("Filters", "篩選")}
                {activeFilterCount > 0 && (
                  <span className="bg-amber-700 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                    {activeFilterCount}
                  </span>
                )}
              </button>
              <p className="text-stone-500 text-xs md:text-sm">
                <strong className="text-stone-900">{filtered.length}</strong>
                <span className="text-stone-400"> / {products.length}</span>
              </p>
              <div
                className="hidden md:flex items-center gap-1 bg-white border border-stone-200 rounded-lg p-1"
                role="group"
                aria-label={b("Thumbnail size", "縮圖大小")}
              >
                {sizeOptions.map(({ cols: n, cells, label }) => (
                  <button
                    key={n}
                    onClick={() => setCols(n)}
                    title={label}
                    aria-label={label}
                    aria-pressed={cols === n}
                    className={`w-8 h-8 flex items-center justify-center rounded-md transition-colors cursor-pointer ${
                      cols === n ? "bg-amber-700 text-white" : "text-stone-400 hover:text-stone-700"
                    }`}
                  >
                    <span
                      className="grid gap-px"
                      style={{
                        gridTemplateColumns: `repeat(${cells}, 1fr)`,
                        width: "15px",
                        height: "15px",
                      }}
                    >
                      {Array.from({ length: cells * cells }).map((_, i) => (
                        <span key={i} className="rounded-[1px] bg-current" />
                      ))}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {filtered.length === 0 ? (
              <div className="text-center py-14 md:py-24">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-stone-100 mx-auto mb-4">
                  <i className="ri-search-line text-2xl text-stone-400" />
                </div>
                <p className="text-stone-700 font-semibold mb-2">
                  {b("No products found", "沒有符合條件的產品")}
                </p>
                <p className="text-stone-400 text-sm">
                  {b("Try adjusting your filters or search term.", "請調整篩選條件或搜尋關鍵字。")}
                </p>
              </div>
            ) : (
              <div className={`grid ${gridClass} gap-5`} data-product-shop>
                {filtered.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ProductsPage;
